import type { ApiErrorPayload, Collection, PaginationMeta } from './types'

const configuredBase = import.meta.env.VITE_API_BASE_URL || '/api/v1'
export const API_BASE_URL = configuredBase.replace(/\/$/, '')
const timeoutMs = Number(import.meta.env.VITE_REQUEST_TIMEOUT_MS || 30_000)

let tokenProvider: () => string | null = () => null
let unauthorizedHandler: ((context: { token: string; path: string }) => void) | null = null
let tokenRefresher: ((context: { token: string; path: string }) => Promise<string | null>) | null = null

export function setTokenProvider(provider: () => string | null) {
  tokenProvider = provider
}

export function setUnauthorizedHandler(handler: ((context: { token: string; path: string }) => void) | null) {
  unauthorizedHandler = handler
}

export function setTokenRefresher(refresher: ((context: { token: string; path: string }) => Promise<string | null>) | null) {
  tokenRefresher = refresher
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: unknown
  readonly traceId?: string

  constructor(status: number, payload: ApiErrorPayload = {}) {
    super(payload.message || defaultErrorMessage(status))
    this.name = 'ApiError'
    this.status = status
    this.code = payload.code || `HTTP_${status}`
    this.details = payload.details
    this.traceId = payload.traceId
  }
}

export class NetworkUnavailableError extends Error {
  constructor(message = 'Нет подключения. Проверьте сеть и повторите действие') {
    super(message)
    this.name = 'NetworkUnavailableError'
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  idempotencyKey?: string
  timeout?: number
  rawResponse?: boolean
  /** `undefined` uses the current app token; `null` deliberately sends no token. */
  authToken?: string | null
  notifyOnUnauthorized?: boolean
  /** @internal A request may be replayed once after its access token is renewed. */
  retriedAfterRefresh?: boolean
}

function defaultErrorMessage(status: number): string {
  if (status === 400) return 'Проверьте заполненные данные'
  if (status === 401) return 'Сессия завершена. Войдите снова'
  if (status === 403) return 'Для этого действия недостаточно прав'
  if (status === 404) return 'Запрошенные данные не найдены'
  if (status === 409 || status === 412) return 'Данные изменились. Сопоставьте версии перед сохранением'
  if (status === 413) return 'Файл слишком большой'
  if (status === 429) return 'Действие временно не отправлено. Повторим автоматически'
  if (status === 422) return 'Некоторые значения требуют исправления'
  if (status >= 500) return 'Данные временно не обновляются. Сохранённые сведения доступны'
  return 'Не удалось выполнить действие'
}

export function isTransientStatus(status: number): boolean {
  return [408, 425, 429].includes(status) || status >= 500
}

function isBodyInit(value: unknown): value is BodyInit {
  return (
    typeof value === 'string' ||
    value instanceof FormData ||
    value instanceof Blob ||
    value instanceof URLSearchParams ||
    value instanceof ArrayBuffer
  )
}

export function buildApiUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE_URL}${normalized}`
}

export function getCurrentAuthToken(): string | null { return tokenProvider() }

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    body: requestedBody,
    idempotencyKey,
    timeout,
    rawResponse,
    authToken,
    notifyOnUnauthorized = true,
    retriedAfterRefresh = false,
    signal: externalSignal,
    ...requestInit
  } = options
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeout ?? timeoutMs)
  const abortFromExternal = () => controller.abort(externalSignal?.reason)
  if (externalSignal?.aborted) abortFromExternal()
  else externalSignal?.addEventListener('abort', abortFromExternal, { once: true })
  const headers = new Headers(options.headers)
  headers.set('Accept', 'application/json')

  const token = authToken === undefined ? tokenProvider() : authToken
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey)

  let body: BodyInit | null | undefined
  if (requestedBody !== undefined && requestedBody !== null) {
    if (isBodyInit(requestedBody)) body = requestedBody
    else {
      headers.set('Content-Type', 'application/json')
      body = JSON.stringify(requestedBody)
    }
  }

  try {
    const response = await fetch(buildApiUrl(path), {
      ...requestInit,
      headers,
      body,
      signal: controller.signal,
    })

    if (!response.ok) {
      let payload: ApiErrorPayload = {}
      try {
        const json = (await response.json()) as { error?: ApiErrorPayload } & ApiErrorPayload
        payload = json.error ?? json
      } catch {
        payload = {}
      }
      if (response.status === 401 && notifyOnUnauthorized && token && !retriedAfterRefresh && !path.endsWith('/auth/login') && !path.endsWith('/auth/refresh')) {
        const renewedToken = await tokenRefresher?.({ token, path })
        if (renewedToken && renewedToken !== token) return apiRequest<T>(path, { ...options, authToken: renewedToken, retriedAfterRefresh: true })
      }
      if (response.status === 401 && notifyOnUnauthorized && token && !path.endsWith('/auth/login')) {
        unauthorizedHandler?.({ token, path })
      }
      throw new ApiError(response.status, payload)
    }

    if (response.status === 204) return undefined as T
    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('json')) return (await response.text()) as T
    const json = await response.json()
    return (rawResponse ? json : unwrapData(json)) as T
  } catch (error) {
    if (error instanceof ApiError) throw error
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new NetworkUnavailableError('Данные не загрузились вовремя. Проверьте сеть и повторите действие')
    }
    throw new NetworkUnavailableError()
  } finally {
    window.clearTimeout(timer)
    externalSignal?.removeEventListener('abort', abortFromExternal)
  }
}

export function unwrapData<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

export function normalizeCollection<T>(payload: unknown, fallbackPage = 1, fallbackLimit = 25): Collection<T> {
  let rawItems: unknown = payload
  let rawMeta: Partial<PaginationMeta> | undefined

  if (payload && typeof payload === 'object' && 'data' in payload) {
    const envelope = payload as { data: unknown; meta?: Partial<PaginationMeta> }
    rawItems = envelope.data
    rawMeta = envelope.meta
  }
  if (rawItems && typeof rawItems === 'object' && !Array.isArray(rawItems) && 'items' in rawItems) {
    const nested = rawItems as { items: unknown; meta?: Partial<PaginationMeta>; pagination?: Partial<PaginationMeta> }
    rawMeta = nested.meta ?? nested.pagination ?? rawMeta
    rawItems = nested.items
  }

  const items = Array.isArray(rawItems) ? (rawItems as T[]) : []
  return {
    items,
    meta: {
      page: Number(rawMeta?.page ?? fallbackPage),
      limit: Number(rawMeta?.limit ?? fallbackLimit),
      total: Number(rawMeta?.total ?? items.length),
    },
  }
}

export async function apiCollection<T>(path: string, options?: RequestOptions): Promise<Collection<T>> {
  const payload = await apiRequest<unknown>(path, { ...options, rawResponse: true })
  return normalizeCollection<T>(payload)
}

export function createIdempotencyKey(prefix = 'web'): string {
  return `${prefix}-${crypto.randomUUID()}`
}

/** Загружает защищённый файл (фото, PDF) с Authorization и возвращает Blob. */
export async function apiBlob(path: string, options: Omit<RequestOptions, 'rawResponse' | 'body'> = {}): Promise<Blob> {
  const { timeout, authToken, signal, retriedAfterRefresh = false, ...requestInit } = options
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeout ?? Math.max(timeoutMs, 120_000))
  const abortFromExternal = () => controller.abort(signal?.reason)
  if (signal?.aborted) abortFromExternal()
  else signal?.addEventListener('abort', abortFromExternal, { once: true })
  const headers = new Headers(options.headers)
  const token = authToken === undefined ? tokenProvider() : authToken
  if (token) headers.set('Authorization', `Bearer ${token}`)
  try {
    const response = await fetch(buildApiUrl(path), { ...requestInit, method: 'GET', headers, signal: controller.signal })
    if (!response.ok) {
      let payload: ApiErrorPayload = {}
      try {
        const json = (await response.json()) as { error?: ApiErrorPayload } & ApiErrorPayload
        payload = json.error ?? json
      } catch {
        payload = {}
      }
      if (response.status === 401 && token && !retriedAfterRefresh && !path.endsWith('/auth/refresh')) {
        const renewedToken = await tokenRefresher?.({ token, path })
        if (renewedToken && renewedToken !== token) return apiBlob(path, { ...options, authToken: renewedToken, retriedAfterRefresh: true })
      }
      if (response.status === 401 && token) unauthorizedHandler?.({ token, path })
      throw new ApiError(response.status, payload)
    }
    return await response.blob()
  } catch (error) {
    if (error instanceof ApiError) throw error
    if (error instanceof DOMException && error.name === 'AbortError') throw new NetworkUnavailableError('Файл не загрузился вовремя. Проверьте сеть и повторите')
    throw new NetworkUnavailableError()
  } finally {
    window.clearTimeout(timer)
    signal?.removeEventListener('abort', abortFromExternal)
  }
}

/** Скачивает защищённый файл в браузере под указанным именем. */
export async function downloadApiFile(path: string, fileName: string, options: Omit<RequestOptions, 'rawResponse' | 'body'> = {}): Promise<void> {
  const blob = await apiBlob(path, options)
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
